import type { SupabaseClient } from '@supabase/supabase-js';
import { markRequest } from '../lib/actionFailureSignals';
import { reportSessionFailure } from '../auth/sessionSignals';

export type ReadScope = {
  eventId?: string | null;
  locationIds?: string[];
  windowStart?: string;
  windowEnd?: string;
};
type Revision = { scope: string; entity: string; domain: string; day: string; revision: string };
type Manifest = { cacheable: boolean; principal: string; business_date: string; versions: Revision[] };
type Entry = { revision: string; data: unknown };
type ClientCache = {
  entries: Map<string, Entry>;
  manifests: Map<string, { until: number; started: number; request: Promise<Manifest> }>;
  loads: Map<string, Promise<unknown>>;
  validations: Map<string, { after: number; started?: number; request: Promise<Manifest> }>;
  generation: number;
  verifiedAuthorization?: string;
};
const caches = new WeakMap<SupabaseClient, ClientCache>();
const activeCaches = new Set<ClientCache>();
const MAX_ENTRIES = 200;
const scopeKey = (scope: ReadScope) => JSON.stringify({ eventId: scope.eventId ?? null, locationIds: [...new Set(scope.locationIds ?? [])].sort(), windowStart: scope.windowStart ?? null, windowEnd: scope.windowEnd ?? null });

function cacheFor(client: SupabaseClient): ClientCache {
  let cache = caches.get(client);
  if (!cache) {
    cache = { entries: new Map(), manifests: new Map(), loads: new Map(), validations: new Map(), generation: 0 };
    caches.set(client, cache);
    activeCaches.add(cache);
    let principal: string | null | undefined;
    client.auth.onAuthStateChange((event, session) => {
      const nextPrincipal = session?.user.id ?? null;
      const changedPrincipal = principal !== undefined && principal !== nextPrincipal;
      principal = nextPrincipal;
      if (event === 'SIGNED_OUT' || changedPrincipal || event === 'USER_UPDATED') {
        cache!.entries.clear(); cache!.manifests.clear(); cache!.loads.clear(); cache!.validations.clear();
        cache!.generation += 1;
        cache!.verifiedAuthorization = undefined;
      }
    });
  }
  return cache;
}

/** A committed write makes the next freshness check read the server again. */
export function invalidateReadManifests(): void {
  activeCaches.forEach(cache => cache.manifests.clear());
}

if (typeof window !== 'undefined') {
  window.addEventListener('portal:write-committed', invalidateReadManifests);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') invalidateReadManifests(); });
}

async function requestReadManifest(client: SupabaseClient, scope: ReadScope, signal?: AbortSignal): Promise<Manifest> {
  const cache = cacheFor(client);
  signal?.throwIfAborted();
  const request = client.rpc('get_portal_read_revision', { p_scope: scope });
  const { data, error } = await markRequest(signal ? request.abortSignal(signal) : request, 'read');
  if (error) {
    if (error.code === '42501' || error.code === 'PGRST301') cache.entries.clear();
    throw error;
  }
  const manifest = data as Manifest;
  const authorization = manifest.cacheable ? JSON.stringify([
    manifest.principal,
    manifest.versions.filter(row => row.domain === 'authorization'),
  ]) : undefined;
  const authorizationChanged = cache.verifiedAuthorization !== undefined
    && authorization !== cache.verifiedAuthorization;
  cache.verifiedAuthorization = authorization;
  if (!manifest.cacheable || authorizationChanged) cache.entries.clear();
  if (authorizationChanged) {
    cache.manifests.clear(); cache.loads.clear(); cache.generation += 1;
    reportSessionFailure({ reason: 'authorization_changed', source: 'query' });
    throw Object.assign(new Error('Your access changed. Reloading your permissions.'), { code: '42501' });
  }
  signal?.throwIfAborted();
  return manifest;
}

async function fetchManifestFor(client: SupabaseClient, scope: ReadScope, afterLoad?: number): Promise<Manifest> {
  const cache = cacheFor(client);
  const key = scopeKey(scope);
  if (cache.manifests.size >= MAX_ENTRIES) {
    for (const [scopeId, value] of cache.manifests) {
      if (value.until <= Date.now()) cache.manifests.delete(scopeId);
    }
  }
  const existing = cache.manifests.get(key);
  if (existing && (afterLoad === undefined
    ? existing.until > Date.now()
    : existing.started > afterLoad)) return existing.request;
  const request = requestReadManifest(client, scope);
  const shared = { until: Number.POSITIVE_INFINITY, started: Date.now(), request };
  cache.manifests.set(key, shared);
  try {
    const result = await request;
    // Share one check across a mounting group, then check again on a revisit.
    shared.until = Date.now() + 750;
    return result;
  } catch (error) {
    if (cache.manifests.get(key) === shared) cache.manifests.delete(key);
    throw error;
  }
}

// Completed reads in one rendering frame share a post-load check. Its actual
// request must start after every participating source read has finished.
async function manifestFor(client: SupabaseClient, scope: ReadScope, afterLoad?: number): Promise<Manifest> {
  if (afterLoad === undefined) return fetchManifestFor(client, scope);
  const cache = cacheFor(client), key = scopeKey(scope), previous = cache.validations.get(key);
  if (previous && (previous.started === undefined || previous.started > afterLoad)) {
    previous.after = Math.max(previous.after, afterLoad);
    return previous.request;
  }
  const generation = cache.generation;
  const validation: { after: number; started?: number; request: Promise<Manifest> } = {
    after: afterLoad,
    request: Promise.resolve(undefined as unknown as Manifest),
  };
  validation.request = (async () => {
    await new Promise<void>(resolve => setTimeout(resolve, 25));
    if (cache.generation !== generation) throw Object.assign(new Error('Read cancelled because your session changed.'), { code: 'PORTAL_READ_CANCELLED' });
    const request = fetchManifestFor(client, scope, validation.after);
    validation.started = cache.manifests.get(key)?.started;
    return request;
  })().finally(() => { if (cache.validations.get(key) === validation) cache.validations.delete(key); });
  cache.validations.set(key, validation);
  return validation.request;
}

function token(manifest: Manifest, domains: readonly string[]): string {
  return JSON.stringify([
    manifest.principal,
    domains.includes('financial') ? manifest.business_date : null,
    // Forecast return risk changes as time passes, even without a write.
    domains.includes('inventory') ? Math.floor(Date.now() / 60_000) : null,
    manifest.versions.filter(row => row.domain === 'authorization' || domains.includes(row.domain)),
  ]);
}

/**
 * Cache successful reads only. Every reuse follows a current authorized check.
 * Changed/cold multi-request loads validate their revision again before being
 * published, so a concurrent source change cannot certify an older result.
 */
export async function revisionedRead<T>(
  client: SupabaseClient,
  scope: ReadScope,
  resource: string,
  domains: readonly string[],
  load: () => Promise<T>,
  options: { cache?: boolean; signal?: AbortSignal } = {},
): Promise<T> {
  const cache = cacheFor(client);
  const generation = cache.generation;
  const key = `${scopeKey(scope)}:${resource}`;
  // A cancellable caller owns independent manifest requests. Its deadline must
  // neither cancel a sibling nor leave a shared manifest promise stuck forever.
  // Default readers retain their shared pre/post checks and cache behavior.
  const readManifest = (afterLoad?: number) => options.signal
    ? requestReadManifest(client, scope, options.signal) : manifestFor(client, scope, afterLoad);
  const manifest = await readManifest();
  options.signal?.throwIfAborted();
  if (!manifest.cacheable) return load();
  const revision = token(manifest, domains);
  // A caller with its own cancellable query cache can require a fresh source
  // read while retaining the same before/after authorization/revision checks.
  const existing = options.cache === false ? undefined : cache.entries.get(key);
  if (existing?.revision === revision) return structuredClone(existing.data) as T;
  const loadKey = `${key}:${revision}`;
  const inFlight = options.cache === false ? undefined : cache.loads.get(loadKey);
  if (inFlight) return structuredClone(await inFlight) as T;
  const operation = (async () => {
    let expected = revision;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const data = await load();
      // Supabase callers that return errors must never cache a failed result.
      if (data && typeof data === 'object' && 'error' in data && data.error) return data;
      options.signal?.throwIfAborted();
      const after = await readManifest(Date.now());
      options.signal?.throwIfAborted();
      if (cache.generation !== generation) throw Object.assign(new Error('Your session changed. Reload this information.'), { code: 'PGRST301' });
      if (!after.cacheable) { cache.entries.delete(key); return load(); }
      const current = token(after, domains);
      if (current === expected) {
        if (options.cache !== false) {
          if (cache.entries.size >= MAX_ENTRIES) cache.entries.delete(cache.entries.keys().next().value!);
          cache.entries.set(key, { revision: current, data: structuredClone(data) });
        }
        return data;
      }
      expected = current;
    }
    throw Object.assign(new Error('This information is changing. Try refreshing it again.'), { code: '40001' });
  })();
  if (options.cache !== false) cache.loads.set(loadKey, operation);
  try { return await operation; }
  finally { if (options.cache !== false) cache.loads.delete(loadKey); }
}
